import React, { useEffect, useMemo, useRef, useState } from 'react';
import Tree from 'react-d3-tree';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import Avatar from 'react-avatar';
import { buildReactD3Tree } from './OrganisationWidgetUtil';
import { ImageFileUtil } from '../../../utils';

function OrganisationWidgetModal({ isOpen, toggle, chart, user }) {
  const treeData = useMemo(() => buildReactD3Tree(chart, user), [chart, user]);
  const normalize = str => str?.toLowerCase().trim();
  const containerRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 400, y: 100 });

  useEffect(() => {
    if (chart || containerRef?.current || isOpen) {
      const centerTree = () => {
        if (!containerRef?.current) return;

        const container = containerRef.current;
        const { offsetWidth, offsetHeight } = container;

        setTranslate({
          x: offsetWidth / 2,
          y: offsetHeight / 6,
        });

        container.scrollLeft =
          (container.scrollWidth - container.clientWidth) / 2;
        container.scrollTop =
          (container.scrollHeight - container.clientHeight) / 2;
      };

      requestAnimationFrame(centerTree);

      window.addEventListener('resize', centerTree);
      return () => window.removeEventListener('resize', centerTree);
    }
    return () => {};
  }, [chart, containerRef, isOpen]);

  const getImage = item => {
    if (item) {
      return ImageFileUtil.getEmployeeFilePath(item);
    }
    return null;
  };

  const renderNode = ({ nodeDatum, toggleNode }) => {
    const isUserNode = normalize(nodeDatum?.attributes?.Code) === user;
    return (
      <foreignObject
        width="clamp(250px, 20vw, 350px)"
        height="clamp(100px, 11vh, 200px)"
        x="calc(clamp(250px, 20vw, 350px) / -2)"
        y="calc(clamp(100px, 11vh, 200px) / -2)"
      >
        <div
          className="org-card-modal-node"
          style={{ backgroundColor: isUserNode ? '#017EBD' : undefined }}
          onClick={toggleNode}
        >
          <div className="d-flex flex-row align-items-center gap-3 p-1">
            <div>
              <Avatar
                size="50px"
                name={nodeDatum?.name || 'Unknown'}
                color="#CFCBDC"
                fgColor="#475467"
                maxInitials={2}
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                }}
                src={getImage(nodeDatum?.attributes?.Imagefile)}
                round
              />
            </div>
            <div className="d-flex flex-column">
              <div
                className="fw-semibold fs-5"
                style={{
                  color: isUserNode ? '#fff' : '#1E7DB2',
                }}
              >
                {nodeDatum?.name}
              </div>
              <div
                className=""
                style={{
                  color: isUserNode ? '#fff' : '#000',
                }}
              >
                {nodeDatum.attributes.Title}
              </div>
            </div>
          </div>
        </div>
      </foreignObject>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      className="org-chart-modal"
    >
      <ModalHeader toggle={toggle}>Organisation Chart</ModalHeader>
      <ModalBody
        className="org-chart-modal-body"
        style={{ height: '80vh', overflow: 'auto' }}
      >
        <div
          ref={containerRef}
          style={{ width: '100%', height: '100%', overflow: 'auto' }}
        >
          {treeData && (
            <Tree
              data={treeData}
              orientation="vertical"
              translate={translate}
              scaleExtent={{ min: 0.3, max: 1 }}
              nodeSize={{ x: 380, y: 150 }}
              pathFunc="step"
              collapsible
              renderCustomNodeElement={renderNode}
              pathClassFunc={() => 'custom-link'}
              zoomable
            />
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}

export default OrganisationWidgetModal;
